import { QueryClient } from '@tanstack/react-query'
import { prefetchBlogPosts } from '@vangst/services/graphcms/marketing/useBlogPosts'
import { Tags } from '@vangst/services/graphcms/types'
import { fetchPage } from '@vangst/services/oogst/page'
import { RobotsEnum } from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import { clsx } from 'clsx'
import { InferGetStaticPropsType } from 'next'
import Image from 'next/image'
import Breadcrumbs from '../components/navigation/Breadcrumbs'
import ClickyExplore from '../components/navigation/ClickyExplore'
import Markdown from '../components/views/Markdown'
import PageHeader from '../components/views/PageHeader'
import Meta from '../domains/application/Meta'
import {
  JobFindSkittle,
  JobHireWithSkittle,
  LearnVangstSkittle,
  Skittles,
} from '../domains/marketing/Skittles'

export async function getStaticProps() {
  const queryClient = new QueryClient()
  const { getPage } = await fetchPage(queryClient, { rawUrl: '/' })
  const page = { ...getPage }
  const crumbs = [{ href: null, text: 'Home' }]

  const varsBlog = {
    first: 1,
    searchTerm: '',
    tags_contains_all: [],
    tags_contains_none: [Tags.Reports],
  }

  const varsReport = {
    first: 1,
    searchTerm: '',
    tags_contains_all: [Tags.Reports],
    tags_contains_none: [],
  }

  const posts = await prefetchBlogPosts(queryClient, varsBlog)
  const reports = await prefetchBlogPosts(queryClient, varsReport)

  const post = posts?.[0] ?? null
  const report = reports?.[0] ?? null

  return {
    props: {
      crumbs,
      post,
      report,
      page,
    },
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function HomePage(props: PropsType) {
  const { crumbs, page, post, report } = props
  const podcast = `<iframe title="podcast" height="152" width="100%" frameborder="no" scrolling="no" seamless src="https://open.spotify.com/embed/show/47Bm6MqajDLc8ETOfBeZxr?utm_source=generator"></iframe>`

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page.metaInfo?.canonicalUrl}
        crumbs={crumbs}
        description={page.metaInfo?.description}
        image={page.pagePhotos?.[0]?.url}
        publishDate={page.postedAt}
        robots={page.metaInfo?.robots ?? RobotsEnum.IndexFollow}
        title={page.metaInfo?.title ?? page.title ?? 'Vangst'}
      />
      <Breadcrumbs className="hidden" crumbs={crumbs} />
      <PageHeader
        title={page.title ?? 'Cannabis Jobs Grow Here.'}
        subtitle={page.subtitle}
        description={page.description}
        imgAlt={page.pagePhotos?.[0]?.alt}
        imgSrc={page.pagePhotos?.[0]?.url}
      />

      <Skittles>
        <JobFindSkittle />
        <LearnVangstSkittle />
        <JobHireWithSkittle />
      </Skittles>

      <div className="contain-y flow-y-md">
        <section className="contain-xyc flow-y-sm mood-white">
          <h2 className="heading-md">Vangst For Job Seekers</h2>
          <ul className="grid gap-16 md:grid-cols-2 lg:grid-cols-3">
            <li className="flow-y-sm">
              <AvatarGraphic
                className="after:bg-blue"
                alt="A man in a red shirt"
                src="https://assets.vangst.com/marketing-site/2023/man-red-shirt.png"
              />
              <div className="flow-y-sm">
                <h3 className="heading-xs">Step #1 Create Your Profile</h3>
                <p className="text-sm">
                  Create your Vangst profile to showcase your experience,
                  skills, and cannabis industry credentials.
                </p>
              </div>
            </li>
            <li className="flow-y-sm">
              <AvatarGraphic
                className="after:bg-green"
                alt="A woman in a flower shirt"
                src="https://assets.vangst.com/marketing-site/2023/lady-flower-shirt.png"
              />
              <div className="flow-y-xs">
                <h3 className="heading-xs">Step #2 Get Credentialed</h3>
                <p className="text-sm">
                  Every state has their own cannabis licensing, badging and
                  training requirements for employees who work in cannabis.{' '}
                  <strong>
                    We’ll help you through this process and provide all the
                    information you need to get badged.
                  </strong>
                </p>
              </div>
            </li>
            <li className="flow-y-sm">
              <AvatarGraphic
                className="after:bg-green-tint"
                alt="A woman in a striped shirt"
                src="https://assets.vangst.com/marketing-site/2023/lady-striped-shirt.png"
              />
              <div className="flow-y-xs">
                <h3 className="heading-xs">Step #3 Apply to Jobs</h3>
                <p className="text-sm">
                  Once your profile and credentials are complete, you’re ready
                  to start applying to cannabis jobs! We’ll help you match into
                  the cannabis jobs that are the best fit for you.
                </p>
              </div>
            </li>
          </ul>
          <ClickyExplore className="mt-auto" href={Routes.SIGN_UP}>
            Create a Vangst Profile
          </ClickyExplore>
        </section>

        <section className="contain-xyc flow-y-sm mood-white">
          <h2 className="heading-md">Vangst For Businesses</h2>
          <ul className="grid gap-16 md:grid-cols-2 lg:grid-cols-3">
            <li className="flow-y-sm">
              <AvatarGraphic
                className="after:bg-blue"
                alt="A man in a black shirt"
                src="https://assets.vangst.com/marketing-site/2023/man-black-shirt.png"
              />
              <div className="flow-y-xs">
                <h3 className="heading-xs">Hire Temporary Employees</h3>
                <h4 className="font-600">Vangst GIGs</h4>
                <p className="text-sm">
                  Vangst GIGS temp staffing agency provides your business access
                  to W2, vetted, credentialed, insured, ready-to-work cannabis
                  temporary employees. Vangst takes care of state and federal
                  taxes, workers comp, payroll, employee management and employee
                  communication. Whether you need trimmers for two months, or
                  budtenders for two days, you can sit back and let Vangst gigs
                  handle all of your temporary cannabis jobs and cannabis
                  staffing, temp to hire, contract and gig jobs staffing needs.
                </p>
              </div>
              <ClickyExplore className="mt-auto" href={Routes.GIGS}>
                Learn More
              </ClickyExplore>
            </li>
            <li className="flow-y-sm">
              <AvatarGraphic
                className="after:bg-green"
                alt="A women in a brown shirt"
                src="https://assets.vangst.com/marketing-site/2023/lady-brown-shirt.png"
              />
              <div className="flow-y-xs">
                <h3 className="heading-xs">Hire Full Time Employees</h3>
                <h4 className="font-600">Vangst Direct Hire</h4>
                <p className="text-sm">
                  <strong>Exceptional Cannabis Staffing.</strong> Our dedicated
                  direct hire team is at the forefront of connecting top talent
                  with leading businesses in the cannabis industry. With a deep
                  understanding of the unique requirements and regulations of
                  the cannabis sector, our team specializes in identifying and
                  placing qualified professionals across various jobs and roles,
                  from cultivation and extraction to retail and compliance. We
                  prioritize finding the perfect match for both cannabis job
                  seekers and cannabis companies and employers, ensuring
                  long-term success and growth within this dynamic and rapidly
                  expanding industry.
                </p>
              </div>
              <ClickyExplore className="mt-auto" href={Routes.DIRECT_HIRE}>
                Learn More
              </ClickyExplore>
            </li>
            <li className="flow-y-sm">
              <AvatarGraphic
                className="after:bg-green-tint"
                alt="A man in a suit"
                src="https://assets.vangst.com/marketing-site/2023/man-suit.png"
              />
              <div className="flow-y-xs">
                <h3 className="heading-xs">Leverage our Platform</h3>
                <p className="text-sm">
                  Create your Vangst company profile to showcase your business
                  and employer brand. From your Vangst dashboard you’ll be able
                  to post cannabis jobs and cannabis job training courses,
                  search and message candidates, and even integrate our platform
                  with your internal ATS.
                </p>
              </div>
              <ClickyExplore className="mt-auto" href={Routes.PLATFORM}>
                Learn More
              </ClickyExplore>
            </li>
          </ul>
        </section>

        <section className="contain-xyc flow-y-sm mood-white">
          <h2 className="heading-md">
            Cannabis Industry Information and Resources
          </h2>
          <ul className="grid gap-16 md:grid-cols-2">
            <li className="flow-y-sm">
              <ImageCover
                alt="An image representing the blog post"
                src={
                  post?.formattedImageUrl ||
                  'https://assets.vangst.com/marketing-site/2023/rolled-joints.png'
                }
              />
              <div className="flow-y-xs">
                <h3 className="heading-xs">{post?.title}</h3>
                <p className="text-sm">{post?.description}</p>
              </div>
              <ClickyExplore className="mt-auto" href={Routes.BLOG_VANGST}>
                View All Blog Articles
              </ClickyExplore>
            </li>
            <li className="flow-y-sm">
              <ImageCover
                alt="An image representing the report"
                src={
                  report?.formattedImageUrl ||
                  'https://assets.vangst.com/marketing-site/2023/little-buds-peach-bg.png'
                }
              />
              <div className="flow-y-xs">
                <h3 className="heading-xs">{report?.title}</h3>
                <p className="text-sm">{report?.description}</p>
              </div>
              <ClickyExplore className="mt-auto" href={Routes.REPORTS}>
                View All Reports & Guides
              </ClickyExplore>
            </li>
            <li className="flow-y-sm">
              <ImageCover
                alt="A Cannabis Growhouse"
                src="https://assets.vangst.com/marketing-site/2023/growhouse.png"
              />
              <div className="flow-y-xs">
                <h3 className="heading-xs">How to Work in Cannabis</h3>
                <p className="text-sm">
                  Politics, budgets, and public support vary widely depending on
                  where you live. We've curated a list of how to get licensed
                  and badged to help clarify what you need to know to land
                  cannabis jobs in each state.
                </p>
              </div>
              <ClickyExplore
                className="mt-auto"
                href={Routes.REPORTS_STATE_REQUIREMENTS}
              >
                View Cannabis Job State Requirements
              </ClickyExplore>
            </li>
            <li className="flow-y-sm">
              <Markdown className="md:min-h-64">{podcast}</Markdown>
              <div className="flow-y-xs">
                <h3 className="heading-xs">
                  Listen to Vangst's signature podcast.
                </h3>
                <p className="text-sm">
                  Join Karson Humiston CEO of Vangst, the cannabis industry’s
                  end-to-end HR technology platform, for conversations with the
                  people behind the cannabis industry.
                </p>
              </div>
              <ClickyExplore
                className="mt-auto"
                href="https://open.spotify.com/show/47Bm6MqajDLc8ETOfBeZxr"
              >
                "Proud to Work in Cannabis" Podcasts
              </ClickyExplore>
            </li>
          </ul>
        </section>
      </div>
    </>
  )
}

// -------------------------------------

export const ImageCover = (props: {
  alt: string
  className?: string
  imgClassName?: string
  src: string
}) => {
  return (
    <div className={clsx('relative h-64 w-full', props?.className)}>
      <Image
        alt={props.alt}
        className={clsx(
          'h-auto max-w-full rounded-2xl object-cover',
          props.imgClassName,
        )}
        fill
        sizes="(min-width: 64em) 50vw, (min-width: 86em) 86em, 100vw"
        src={props.src}
      />
    </div>
  )
}

export const AvatarGraphic = (props: {
  className?: string
  imgClassName?: string
  alt?: string
  src?: string
}) => {
  return (
    <div
      className={clsx(
        'relative flex h-64 w-full',
        'after:absolute after:inset-x-8 after:bottom-0 after:z-0 after:h-1/2 after:rounded-tr-2xl',
        props?.className,
      )}
    >
      {props.alt && props.src && (
        <Image
          alt={props.alt}
          className={clsx(
            'z-10 h-auto max-w-full object-contain',
            props?.imgClassName,
          )}
          fill
          sizes="(min-width: 64em) 50vw, (min-width: 86em) 86em, 100vw"
          src={props.src}
        />
      )}
    </div>
  )
}
